import React, { useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import { safeAccessibilityRole } from '@/constants/utils/a11y';

const isWeb = Platform.OS === 'web';

const FACTS = [
  { icon: 'bullseye', titleKey: 'pages.agents.intro.fact1Title', bodyKey: 'pages.agents.intro.fact1Body' },
  { icon: 'comments', titleKey: 'pages.agents.intro.fact2Title', bodyKey: 'pages.agents.intro.fact2Body' },
  { icon: 'wand-magic-sparkles', titleKey: 'pages.agents.intro.fact3Title', bodyKey: 'pages.agents.intro.fact3Body' },
];

function AgentsIntroHero({ personalContent }) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();
  const compact = isXS || isSM;
  const [showDetails, setShowDetails] = useState(false);
  const palette = getCatalogPalette(theme);
  const styles = useMemo(() => createStyles(compact, palette), [compact, palette]);

  return (
    <View style={styles.wrap}>
      <View style={styles.hero} accessibilityRole={safeAccessibilityRole('banner')}>
        <View style={styles.intro}>
          <View style={styles.titleRow}>
            <Text tag="h1" weight="bold" style={styles.headline}>{t('navigation.agents')}</Text>
            <Pressable accessibilityRole="button" accessibilityLabel={t('pages.agents.aboutBots')}
              accessibilityState={{ expanded: showDetails }}
              {...(isWeb ? { 'aria-expanded': showDetails } : {})}
              onPress={() => setShowDetails(value => !value)}
              style={({ hovered, pressed }) => [styles.about, (hovered || pressed || showDetails) && { backgroundColor: palette.hover }]}>
              <Icon name="circle-info" size={13} color={colors.text_color_secondary} />
              <Text size="xs" color="secondary">{t('pages.agents.aboutBots')}</Text>
            </Pressable>
          </View>
          <Text tag="p" color="secondary" style={styles.lead}>{t('pages.agents.intro.lead')}</Text>
        </View>

        {showDetails && <View style={styles.facts} accessibilityRole={safeAccessibilityRole('list')}>
          {FACTS.map((fact, index) => (
            <View key={fact.titleKey} style={[styles.fact, index === 0 && styles.factFirst]} accessibilityRole={safeAccessibilityRole('listitem')}>
              <View style={styles.factIcon}>
                <Icon name={fact.icon} size={15} color={palette.icon} />
              </View>
              <View style={styles.factCopy}>
                <Text size="s" weight="semibold">{t(fact.titleKey)}</Text>
                <Text size="xs" color="secondary" style={styles.factBody}>{t(fact.bodyKey)}</Text>
              </View>
            </View>
          ))}
        </View>}
      </View>

      {personalContent}
    </View>
  );
}

AgentsIntroHero.propTypes = {
  personalContent: PropTypes.node,
};

const createStyles = (compact, palette) => StyleSheet.create({
  wrap: { width: '100%', paddingHorizontal: compact ? 16 : 24, paddingTop: compact ? 20 : 28 },
  hero: {
    marginBottom: 24,
  },
  intro: { minWidth: 0, gap: 8, maxWidth: 640 },
  titleRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 14 },
  about: { flexDirection: 'row', alignItems: 'center', gap: 6, padding: 8, borderRadius: 8 },
  headline: { fontSize: 32, lineHeight: 38, letterSpacing: -0.8 },
  lead: { fontSize: 14, lineHeight: 21, maxWidth: 600 },
  facts: {
    flexDirection: compact ? 'column' : 'row',
    minWidth: 0,
    borderWidth: 1,
    borderColor: palette.border,
    backgroundColor: palette.surface,
    borderRadius: 14,
    marginTop: 18,
    overflow: 'hidden',
  },
  fact: {
    flex: compact ? undefined : 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 16,
    borderTopWidth: compact ? 1 : 0,
    borderLeftWidth: compact ? 0 : 1,
    borderColor: palette.border,
  },
  factFirst: { borderTopWidth: 0, borderLeftWidth: 0 },
  factIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.preview,
    marginTop: 1,
  },
  factCopy: { flex: 1, minWidth: 0, gap: 4 },
  factBody: { lineHeight: 20 },
});

export default React.memo(AgentsIntroHero);
